<template>
    <div
        ref="container"
        class="mc-supply-chain-ocr--view"
    >
        <img
            v-if="type !== 'html'&&originalImageSrc"
            ref="image"
            :src="originalImageSrc"
            alt="Original Image"
            draggable="false"
            class="unselectable"
            @load="onImageLoad"
        />
        <div
            v-if="type === 'ocr'"
            class="zoom-controls"
        >
            <div
                class="zoom-btn"
                :class="{ disabled: zoomLevel <= zoomRange[0] }"
                @click="handleZoom('out')"
            >
                <i class="mtdicon-micrify"></i>
            </div>
            <div
                class="zoom-btn"
                :class="{ disabled: zoomLevel >= zoomRange[1] }"
                @click="handleZoom('in')"
            >
                <i class="mtdicon-enlarge"></i>
            </div>

            <div
                class="show-ocr-btn"
                @click="showOcrResult = !showOcrResult"
            >
                是否展示ocr结果
            </div>
        </div>
        <template v-if="type === 'html'">
            <div
                class="table-container"
                v-html="parsedHtmlContent"
            ></div>
        </template>
        <div
            v-else
            v-show="showOcrResult"
            class="render-result"
            @mousedown.left="startSelection"
            @mousemove.left="updateSelection"
            @mouseup.left="endSelection"
            @mouseleave.left="endSelection"
        >
            <template v-for="item in ocrItems">
                <div
                    :key="`box-${item.sortIndex}`"
                    :style="getBoxStyle(item)"
                    :class="['ocr-box', { selected: item.selected }]"
                    @click="toggleBox(item)"
                >
                    <div
                        class="ocr-text"
                    >
                        {{ item.text }}
                    </div>
                </div>
            </template>
        </div>
        <div
            v-if="isSelecting"
            :style="selectionBoxStyle"
            class="selection-box"
        ></div>
        <div
            v-show="showContextMenu"
            :style="contextMenuStyle"
            class="context-menu"
        >
            <div
                class="menu-item"
                @click="handleClear"
            >
                取消选择
            </div>
            <div
                class="menu-item"
                @click="handleNormalCopy"
            >
                普通复制
            </div>
            <div
                class="menu-item"
                @click="handleNutritionCopy"
            >
                复制为营养成分表格式
            </div>
        </div>
    </div>
</template>

<script>
// import './mc-supply-chain-ocr-view.scss';
import { getCookie } from '@mcfe/tools';
import { reportLx } from '@lsfe/tools';

export default {
    name: 'McSupplyChainOcrView',
    props: {
        ocrResult: {
            type: Object,
            required: true,
        },
        originalImageSrc: {
            type: String,
        },
        type: {
            type: String,
            default: 'ocr',
            validator: (value) => ['ocr', 'html'].includes(value),
        },
        zoomRange: {
            type: Object,
            default: () => ({ min: 0.5, max: 2, step: 0.2 }),
        },
    },
    data() {
        return {
            ocrItems: [],
            baseScale: 1,
            isSelecting: false,
            selectionStart: { x: 0, y: 0 },
            selectionEnd: { x: 0, y: 0 },
            lastProcessedOcrResult: null,
            showContextMenu: false,
            contextMenuPosition: { x: 0, y: 0 },
            zoomLevel: 1,
            showOcrResult: true,
        };
    },
    computed: {
        selectionBoxStyle() {
            const left = Math.min(this.selectionStart.x, this.selectionEnd.x);
            const top = Math.min(this.selectionStart.y, this.selectionEnd.y);
            const width = Math.abs(this.selectionEnd.x - this.selectionStart.x);
            const height = Math.abs(this.selectionEnd.y - this.selectionStart.y);
            return {
                left: `${left}px`,
                top: `${top}px`,
                width: `${width}px`,
                height: `${height}px`,
            };
        },
        contextMenuStyle() {
            return {
                position: 'fixed',
                left: `${this.contextMenuPosition.x}px`,
                top: `${this.contextMenuPosition.y}px`,
            };
        },
        parsedHtmlContent() {
            if (this.type !== 'html' || !this.ocrResult.res) return '';
            try {
                const parsed = JSON.parse(this.ocrResult.res);
                const htmlContent = parsed[0]?.HTML || '';
                return `<table>${htmlContent}</table>`;
            } catch (e) {
                console.error('Failed to parse HTML content:', e);
                return '';
            }
        },
    },
    watch: {
        ocrResult: {
            handler(newValue) {
                if (newValue !== this.lastProcessedOcrResult) {
                    this.$nextTick(() => {
                        this.calculateScale();
                        this.processOcrResult();
                    });
                    this.lastProcessedOcrResult = newValue;
                }
            },
            deep: true,
        },
    },
    mounted() {
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('click', this.hideContextMenu);
        this.$refs.container.addEventListener('contextmenu', this.showCustomContextMenu);
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('click', this.hideContextMenu);
        this.$refs.container.removeEventListener('contextmenu', this.showCustomContextMenu);
    },
    methods: {
        clearSelect() {
            this.handleClear();
            this.hideContextMenu();
            navigator.clipboard.writeText('');
        },
        onImageLoad() {
            this.$nextTick(this.calculateScale);
        },
        calculateScale() {
            const image = this.$refs.image;
            if (!image) return;

            const originalWidth = this.ocrResult.shape_info[0];
            const scaleWidth = image.offsetWidth;
            const naturalWidth = image.naturalWidth;
            this.baseScale = (naturalWidth && originalWidth) ? scaleWidth / naturalWidth : 1;

            this.processOcrResult();
        },
        processOcrResult() {
            if (!this.baseScale) return;

            const totalScale = this.baseScale * this.zoomLevel;

            this.ocrItems = this.ocrResult.results_str.map((text, index) => {
                const [x, y, x2, y2] = this.parseLocation(this.ocrResult.location[index]);
                return {
                    sortIndex: index,
                    text,
                    x: x * totalScale,
                    y: y * totalScale,
                    width: (x2 - x) * totalScale,
                    height: (y2 - y) * totalScale,
                    selected: false,
                };
            });
        },
        parseLocation(locationStr) {
            // Extract all numbers from the location string
            const numbers = locationStr.match(/\d+/g).map(Number);

            // Get the bounding box coordinates
            const xCoords = [numbers[0], numbers[2], numbers[4], numbers[6]];
            const yCoords = [numbers[1], numbers[3], numbers[5], numbers[7]];

            // Calculate the bounding box
            const x = Math.min(...xCoords);
            const y = Math.min(...yCoords);
            const x2 = Math.max(...xCoords);
            const y2 = Math.max(...yCoords);

            return [x, y, x2, y2];
        },
        getBoxStyle(item) {
            return {
                left: `${item.x}px`,
                top: `${item.y}px`,
                width: `${item.width}px`,
                height: `${item.height}px`,
            };
        },
        toggleBox(item) {
            item.selected = !item.selected;
        },
        startSelection(event) {
            if (event.button !== 0 || this.showContextMenu) {
                return;
            }

            this.isSelecting = true;
            const rect = this.$refs.container.getBoundingClientRect();
            this.selectionStart = {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
            };
            this.selectionEnd = { ...this.selectionStart };

            event.preventDefault();
            event.stopPropagation();
        },
        updateSelection(event) {
            if (!this.isSelecting) return;

            const rect = this.$refs.container.getBoundingClientRect();
            this.selectionEnd = {
                x: Math.max(0, Math.min(event.clientX - rect.left, rect.width)),
                y: Math.max(0, Math.min(event.clientY - rect.top, rect.height)),
            };

            event.preventDefault();
            event.stopPropagation();
        },
        endSelection() {
            if (!this.isSelecting) return;

            this.isSelecting = false;
            if (
                Math.abs(this.selectionEnd.x - this.selectionStart.x) > 5
                || Math.abs(this.selectionEnd.y - this.selectionStart.y) > 5
            ) {
                this.selectBoxesInRange();
            }

            this.selectionStart = { x: 0, y: 0 };
            this.selectionEnd = { x: 0, y: 0 };
        },
        selectBoxesInRange() {
            const {
                left, top, right, bottom,
            } = this.getSelectionBounds();

            const itemsInRange = this.ocrItems.filter((item) => this.isBoxInSelection(item, left, top, right, bottom));

            if (itemsInRange.length > 0) {
                itemsInRange.forEach((item) => {
                    item.selected = true;
                });
            }
        },
        getSelectionBounds() {
            return {
                left: Math.min(this.selectionStart.x, this.selectionEnd.x),
                top: Math.min(this.selectionStart.y, this.selectionEnd.y),
                right: Math.max(this.selectionStart.x, this.selectionEnd.x),
                bottom: Math.max(this.selectionStart.y, this.selectionEnd.y),
            };
        },
        isBoxInSelection(item, left, top, right, bottom) {
            return (
                item.x < right
          && item.x + item.width > left
          && item.y < bottom
          && item.y + item.height > top
            );
        },
        copySelectedText() {
            const selectedItems = this.ocrItems.filter((item) => item.selected);
            if (!selectedItems.length) return;

            const sortedRows = this.sortSelectedItems(selectedItems);
            const selectedText = sortedRows
                .map((row) => row.map((item) => item.text).join(' '))
                .join('\n');

            if (selectedText) {
                navigator.clipboard.writeText(selectedText).then(() => {
                    console.log('Selected text copied to clipboard');
                }).catch((err) => {
                    console.error('Failed to copy text: ', err);
                });
            }
        },
        handleKeyDown(event) {
            if ((event.metaKey || event.ctrlKey) && event.key === 'v') {
                this.ocrItems.forEach((item) => {
                    item.selected = false;
                });
            }
        },
        showCustomContextMenu(event) {
            if (this.type !== 'html') {
                event.preventDefault();
                this.showContextMenu = true;
                this.contextMenuPosition = {
                    x: event.clientX,
                    y: event.clientY,
                };
            }
        },
        hideContextMenu() {
            this.showContextMenu = false;
        },
        handleClear() {
            this.ocrItems.forEach((item) => {
                item.selected = false;
            });
        },
        handleNormalCopy() {
            reportLx('moduleClick', 'b_chaoshi_kjaimq2r_mc', { msid: getCookie('msid') || '' }, { cid: 'c_chaoshi_wg348euq' });
            this.copySelectedText();
            this.hideContextMenu();
            this.handleClear();
        },
        handleNutritionCopy() {
            reportLx('moduleClick', 'b_chaoshi_14r4lluh_mc', { msid: getCookie('msid') || '' }, { cid: 'c_chaoshi_wg348euq' });
            const selectedItems = this.ocrItems.filter((item) => item.selected);
            if (!selectedItems.length) return;

            const sortedRows = this.sortSelectedItems(selectedItems);
            const nutritionFormat = sortedRows.flatMap((row) => row.map((item) => item.text));

            navigator.clipboard.writeText(JSON.stringify(nutritionFormat))
                .then(() => {
                    console.log('Nutrition format copied to clipboard');
                })
                .catch((err) => {
                    console.error('Failed to copy nutrition format: ', err);
                });

            this.hideContextMenu();
            this.handleClear();
        },
        sortSelectedItems(items) {
            const yTolerance = 5;
            const rows = [];

            items.forEach((item) => {
                const itemCenterY = item.y + item.height / 2;
                let row = rows.find((r) => {
                    const rowCenterY = r[0].y + r[0].height / 2;
                    return Math.abs(rowCenterY - itemCenterY) <= yTolerance;
                });

                if (!row) {
                    row = [];
                    rows.push(row);
                }
                row.push(item);
            });

            // 对每一行按x坐标从左到右排序
            rows.forEach((row) => {
                row.sort((a, b) => a.x - b.x);
            });

            // 将所有行按y坐标从上到下排序
            rows.sort((a, b) => a[0].y - b[0].y);

            return rows;
        },
        handleZoom(type) {
            const { min: MIN_ZOOM, max: MAX_ZOOM, step: ZOOM_STEP } = this.zoomRange;

            // 如果已经达到限制，直接返回
            if (type === 'in' && this.zoomLevel >= MAX_ZOOM) return;
            if (type === 'out' && this.zoomLevel <= MIN_ZOOM) return;

            const oldZoom = this.zoomLevel;

            if (type === 'in') {
                this.zoomLevel = Math.min(this.zoomLevel + ZOOM_STEP, MAX_ZOOM);
            } else if (type === 'out') {
                this.zoomLevel = Math.max(this.zoomLevel - ZOOM_STEP, MIN_ZOOM);
            }

            // 确保缩放值符合步进值
            this.zoomLevel = Math.round(this.zoomLevel / ZOOM_STEP) * ZOOM_STEP;

            if (oldZoom !== this.zoomLevel) {
                this.updateZoom();
            }
        },

        updateZoom() {
            const image = this.$refs.image;
            if (image) {
                image.style.transform = `scale(${this.zoomLevel})`;
                image.style.transformOrigin = 'top left';
            }

            this.processOcrResult();
        },
    },
};
</script>

<style lang="scss" scoped>
.mc-supply-chain-ocr--view {
    position: relative;
    display: inline-block;
    width: 100%;
    .table-container {
        max-width: 100%;
        overflow-x: auto;
        table {
            border-collapse: collapse;
            width: 100%;
            margin: 0 auto;
            border: 1px solid #ccc;
        }
        td,
        th {
            border: 1px solid #ccc;
            padding: 8px;
            text-align: center;
        }
        thead td {
            background-color: #f2f2f2;
            font-weight: bold;
        }
    }
    img.unselectable {
        user-select: none;
        -moz-user-select: none;
        -webkit-user-select: none;
        -ms-user-select: none;
        pointer-events: none;
    }
    .render-result {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: auto;
        transform-origin: top left;
        transform: scale(1);
    }
    .ocr-box {
        position: absolute;
        background-color: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        border: 1px solid red;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        &.selected {
            border: 1px solid blue;
            background-color: rgba(0, 0, 255, 0.1);
        }
    }
    .ocr-text {
        zoom: 0.5;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        user-select: none;
        pointer-events: none;
        padding: 2px;
        width: 100%;
        color: transparent;
    }
    .selection-box {
        position: absolute;
        border: 1px solid blue;
        background-color: rgba(0, 0, 255, 0.1);
        pointer-events: none;
    }
    .context-menu {
        position: fixed;
        background: white;
        border: 1px solid #ccc;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 255, 0.15);
        z-index: 1000;
    }
    .menu-item {
        padding: 8px 16px;
        cursor: pointer;
        user-select: none;
        &:hover {
            background-color: #f5f5f5;
        }
    }
    .zoom-controls {
        position: relative; // 改为相对定位
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-top: 8px; // 添加上边距
        z-index: 1000;
    }
    .zoom-btn {
        width: 32px;
        height: 32px;
        border-radius: 4px;
        background-color: rgba(0, 0, 0, 0.3);
        border: none;
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.3s;
        outline: none;
        user-select: none;
        .icon-zoom-out,
        .icon-zoom-in {
            line-height: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            font-weight: bold;
        }
        &:hover {
            background-color: rgba(0, 0, 0, 0.5);
        }
    }
    i {
        font-size: 30px;
    }
    .show-ocr-btn {
        display: inline-block;
        line-height: 2;
        white-space: nowrap;
        cursor: pointer;
        background: #409eff;
        border: 1px solid #dcdfe6;
        color: #fff;
        text-align: center;
        box-sizing: border-box;
        outline: none;
        margin: 0;
        border-radius: 4px;
        transition: .1s;
        padding: 0 10px;
        font-weight: 500;
    }
}
</style>
